import React, { useEffect, useState } from 'react';
import { Table, Input } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { CompanyClassificationDto } from '../../../models/CompanyClassifications';
import { Button } from '@prio365/prio365-react-library';

const useStyles = makePrioStyles((theme) => ({
  root: {
    '& .ant-table-cell > a': {
      color: theme.old.typography.colors.base,
    },
    '& .ant-table-row .ant-table-cell .company-classification-delete-button': {
      visibility: 'hidden',
    },
    '& .ant-table-row:hover .ant-table-cell .company-classification-delete-button':
      {
        visibility: 'visible',
      },
  },
}));

type CompanyClassificationWithDirty = CompanyClassificationDto & {
  isDirty?: boolean;
};

interface CompanyClassificationTableProps {
  companyClassifications: CompanyClassificationWithDirty[];
  setUpdatedClassifications: (
    updated: CompanyClassificationWithDirty[]
  ) => void;
  deleteCompanyClassification: (id: string) => void;
  className?: string;
}

const CompanyClassificationTable: React.FC<CompanyClassificationTableProps> = ({
  companyClassifications,
  setUpdatedClassifications,
  deleteCompanyClassification,
  className,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [data, setData] = useState<CompanyClassificationWithDirty[]>([]);

  useEffect(() => {
    setData(companyClassifications);
  }, [companyClassifications]);

  // Update a specific field and mark as dirty
  const updateField = (index: number, key: string, value: any) => {
    const updated = data.map((item, i) =>
      i === index ? { ...item, [key]: value, isDirty: true } : item
    );

    setData(updated);
    setUpdatedClassifications(updated);
  };

  // Handle row deletion
  const handleDelete = (name: string) => {
    deleteCompanyClassification(name);
  };

  const columns: ColumnProps<CompanyClassificationWithDirty>[] = [
    {
      title: t('settings:companyClassification.table.name'),
      dataIndex: 'name',
      render: (text, _, index) => (
        <Input
          value={text}
          onChange={(e) => updateField(index, 'name', e.target.value)}
        />
      ),
    },
    {
      title: t('settings:companyClassification.table.description'),
      dataIndex: 'description',
      render: (text, _, index) => (
        <Input
          value={text}
          onChange={(e) => updateField(index, 'description', e.target.value)}
        />
      ),
    },

    {
      title: '',
      render: (_, record) => (
        <Button
          type="link"
          className="company-classification-delete-button"
          onClick={() => handleDelete(record.name)}
          iconProp={['fal', 'trash']}
        ></Button>
      ),
    },
  ];

  return (
    <Table
      className={classNames(classes.root, className)}
      dataSource={data.map((item, index) => ({ ...item, key: index }))}
      columns={columns}
      pagination={false}
    />
  );
};

export default CompanyClassificationTable;
